import React, { useRef, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useApproveHeatmapSurchargesBulk } from '~/hooks/apis/lere/useApproveHeatmapSurchargesBulk';

import { HeatmapSurchargeApproval, HeatmapSurchargeBulkApprovalResponse } from '~/types/heatmapSurcharge';

import currencyFormatter from '~/utils/currencyFormatter';

interface Props {
  approvals: Array<HeatmapSurchargeApproval>;
  roomTypeName: string;
  onClose: () => void;
  onSubmitted: (response: HeatmapSurchargeBulkApprovalResponse) => void;
}

export default function HeatmapBulkApprovalDialog({ approvals, roomTypeName, onClose, onSubmitted }: Props) {
  const [note, setNote] = useState('');
  const [result, setResult] = useState<HeatmapSurchargeBulkApprovalResponse>();
  const mutation = useApproveHeatmapSurchargesBulk();
  const submitting = useRef(false);

  async function confirm() {
    if (submitting.current) return;
    submitting.current = true;
    try {
      const response = await mutation.mutateAsync({ approvals, note: note.trim() });
      setResult(response);
      onSubmitted(response);
    } catch {
      // The mutation error stays in the dialog; never retry a price action automatically.
    } finally {
      submitting.current = false;
    }
  }

  const nonSubmittedResults = result?.results.filter((item) => item.status !== 'submitted') ?? [];

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{result ? 'Bulk heatmap surcharge result' : `Review ${approvals.length} surcharges`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography fontWeight="bold">{roomTypeName}</Typography>
          {!result && (
            <>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader aria-label="Selected heatmap surcharge dates">
                  <TableHead>
                    <TableRow>
                      <TableCell>Check-in date</TableCell>
                      <TableCell align="right">Recommendation</TableCell>
                      <TableCell align="right">Additional surcharge</TableCell>
                      <TableCell align="right">New nightly price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvals.map(({ quote }) => (
                      <TableRow key={quote.travelDate}>
                        <TableCell>{dayjs(quote.travelDate).format('D MMM YYYY (ddd)')}</TableCell>
                        <TableCell align="right">{quote.suggestedSurchargePercentage}% of base</TableCell>
                        <TableCell align="right">
                          +{currencyFormatter('AUD', quote.additionalInternalSurcharge, 2)}
                        </TableCell>
                        <TableCell align="right">{currencyFormatter('AUD', quote.proposedNightlyPrice, 2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info">
                Each date is checked again before it is queued. Dates that changed, already meet their target, or were
                already submitted will be skipped without blocking the remaining dates. Existing room-price spacing
                rules may also adjust higher room types.
              </Alert>
              <TextField
                label="Note for all selected dates (optional)"
                multiline
                minRows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                inputProps={{ maxLength: 1000 }}
                helperText={`${note.length}/1000`}
                disabled={mutation.isPending}
              />
            </>
          )}
          {mutation.isError && (
            <Alert severity="error">
              {mutation.error.message} Refresh the calendar before deciding whether to retry.
            </Alert>
          )}
          {result && (
            <>
              <Alert severity={result.failedCount ? 'error' : result.skippedCount ? 'warning' : 'success'}>
                {result.submittedCount} queued, {result.skippedCount} skipped, {result.failedCount} failed.
              </Alert>
              {nonSubmittedResults.map((item) => (
                <Alert key={item.travelDate} severity={item.status === 'failed' ? 'error' : 'warning'}>
                  {dayjs(item.travelDate).format('D MMM YYYY')}: {item.message || 'Not submitted.'}
                </Alert>
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" disabled={mutation.isPending} onClick={onClose}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            disabled={mutation.isPending || mutation.isSuccess || mutation.isError}
            onClick={confirm}
          >
            {mutation.isPending ? 'Submitting…' : `Apply ${approvals.length} surcharges`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
